import React from 'react';
import { apiConfig } from '../../utils/apiConfig';

interface Props {
  onLinkClick?: () => void; // Optional: closes the drawer on mobile
  activeSection?: string;   // The id of the currently active section
}

const SidebarNav: React.FC<Props> = ({ onLinkClick, activeSection }) => {
  const { endpoints } = apiConfig;
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(
    new Set([
      'api-reference',
      'behavior',
      'error-handling',
      'generate-presentation',
      'generate-custom-slide',
      'generate-slide-narrative',
    ])
  );

  const toggleSection = (sectionId: string) => {
    const next = new Set(collapsedSections);
    if (next.has(sectionId)) next.delete(sectionId);
    else next.add(sectionId);
    setCollapsedSections(next);
  };

  // Smooth, reliable SPA hash navigation
  const handleHashNav = React.useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const hash = `#${id}`;
        if (window.location.hash !== hash) {
          window.history.replaceState(null, '', hash);
        }
      }
      onLinkClick?.(); // close drawer on mobile if provided
    },
    [onLinkClick]
  );

  const getEndpointSubsections = (endpoint: any) => {
    const subsections: { id: string; title: string }[] = [];

    if (endpoint.headers?.length) subsections.push({ id: `${endpoint.id}-headers`, title: 'Headers' });
    if (endpoint.bodyParams?.length) subsections.push({ id: `${endpoint.id}-body-parameters`, title: 'Body Parameters' });
    if (endpoint.method && endpoint.request) subsections.push({ id: `${endpoint.id}-request-body`, title: 'Request Body' });
    if (endpoint.response) subsections.push({ id: `${endpoint.id}-response-body`, title: 'Response Body' });

    if (endpoint.modes?.length) {
      endpoint.modes.forEach((mode: any) => {
        if (mode.request) subsections.push({ id: `${endpoint.id}-${mode.id}-request-body`, title: `${mode.modeTitle} - Request` });
        if (mode.response) subsections.push({ id: `${endpoint.id}-${mode.id}-response-body`, title: `${mode.modeTitle} - Response` });
      });
    }

    subsections.push({ id: `${endpoint.id}-examples`, title: 'API Examples' });
    return subsections;
  };

  return (
    <nav>
      <ul className="space-y-4">
        <li>
          <a
            href="#overview"
            className={`font-medium transition-colors duration-200 ${
              activeSection === 'overview' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
            }`}
            onClick={(e) => handleHashNav(e, 'overview')}
          >
            Overview
          </a>
        </li>

        <li>
          <a
            href="#getting-started"
            className={`font-medium transition-colors duration-200 ${
              activeSection === 'getting-started' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
            }`}
            onClick={(e) => handleHashNav(e, 'getting-started')}
          >
            Getting Started
          </a>
        </li>

        <li>
          <div className="flex items-center">
            <button
              onClick={() => toggleSection('api-reference')}
              className="mr-2 text-black hover:text-black transition-colors"
            >
              {collapsedSections.has('api-reference') ? '▶' : '▼'}
            </button>
            <a
              href="#api-reference"
              className={`font-medium transition-colors duration-200 ${
                activeSection === 'api-reference' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
              }`}
              onClick={(e) => handleHashNav(e, 'api-reference')}
            >
              API Reference
            </a>
          </div>

          {!collapsedSections.has('api-reference') && (
            <ul className="pl-4 mt-2 space-y-4 border-l border-slate-200">
              {endpoints.map((endpoint: any) => {
                const subsections = getEndpointSubsections(endpoint);
                const isEndpointActive = activeSection === endpoint.id;
                const isAnySubActive = subsections.some((s) => activeSection === s.id);
                const endpointCollapsed = collapsedSections.has(endpoint.id);

                return (
                  <li key={endpoint.id} className="pb-2">
                    <div className="flex items-center mb-3">
                      <button
                        onClick={() => toggleSection(endpoint.id)}
                        className="mr-2 text-black hover:text-black transition-colors text-xs"
                      >
                        {endpointCollapsed ? '▶' : '▼'}
                      </button>
                      <a
                        href={`#${endpoint.id}`}
                        className={`text-sm font-medium transition-colors duration-200 ${
                          isEndpointActive || isAnySubActive
                            ? 'text-blue-600 font-semibold'
                            : 'text-slate-600 hover:text-blue-600'
                        }`}
                        onClick={(e) => handleHashNav(e, endpoint.id)}
                      >
                        {endpoint.title}
                      </a>
                    </div>

                    {!endpointCollapsed && (
                      <ul className="pl-3 space-y-1.5 border-l border-slate-100">
                        {subsections.map((sub) => {
                          const isSubActive = activeSection === sub.id;
                          return (
                            <li key={sub.id}>
                              <a
                                href={`#${sub.id}`}
                                className={`text-xs block py-1 transition-all duration-200 ${
                                  isSubActive
                                    ? 'text-blue-600 font-semibold pl-2 bg-blue-50 rounded-sm'
                                    : 'text-slate-400 hover:text-blue-500 hover:pl-2 hover:text-slate-600'
                                }`}
                                onClick={(e) => handleHashNav(e, sub.id)}
                              >
                                • {sub.title}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </li>

        <li>
          <div className="flex items-center">
            <button
              onClick={() => toggleSection('behavior')}
              className="mr-2 text-black hover:text-black transition-colors"
            >
              {collapsedSections.has('behavior') ? '▶' : '▼'}
            </button>
            <a
              href="#parameter-behavior"
              className={`font-medium transition-colors duration-200 ${
                activeSection === 'parameter-behavior' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
              }`}
              onClick={(e) => handleHashNav(e, 'parameter-behavior')}
            >
              Parameter Behavior
            </a>
          </div>

          {!collapsedSections.has('behavior') && (
            <ul className="pl-4 mt-2 space-y-2 border-l border-slate-200">
              <li>
                <a
                  href="#parameter-library"
                  className={`text-sm block py-1 transition-colors duration-200 ${
                    activeSection === 'parameter-library'
                      ? 'text-blue-600 font-semibold pl-2 bg-blue-50 rounded-sm'
                      : 'text-slate-600 hover:text-blue-500 hover:pl-2'
                  }`}
                  onClick={(e) => handleHashNav(e, 'parameter-library')}
                >
                  • Parameter Library
                </a>
              </li>
              <li>
                <a
                  href="#presentation-id-behavior"
                  className={`text-sm block py-1 transition-colors duration-200 ${
                    activeSection === 'presentation-id-behavior'
                      ? 'text-blue-600 font-semibold pl-2 bg-blue-50 rounded-sm'
                      : 'text-slate-600 hover:text-blue-500 hover:pl-2'
                  }`}
                  onClick={(e) => handleHashNav(e, 'presentation-id-behavior')}
                >
                  • Presentation ID Behavior
                </a>
              </li>
            </ul>
          )}
        </li>

        <li>
          <div className="flex items-center">
            <button
              onClick={() => toggleSection('error-handling')}
              className="mr-2 text-black hover:text-black transition-colors"
            >
              {collapsedSections.has('error-handling') ? '▶' : '▼'}
            </button>
            <a
              href="#error-handling"
              className={`font-medium transition-colors duration-200 ${
                activeSection === 'error-handling' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
              }`}
              onClick={(e) => handleHashNav(e, 'error-handling')}
            >
              Error Handling
            </a>
          </div>
        </li>

        {/* API Pricing */}
        <li>
          <a
            href="#api-pricing"
            className={`font-medium transition-colors duration-200 ${
              activeSection === 'api-pricing' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
            }`}
            onClick={(e) => handleHashNav(e, 'api-pricing')}
          >
            API Pricing
          </a>
        </li>

        <li>
          <a
            href="#support"
            className={`font-medium transition-colors duration-200 ${
              activeSection === 'support' ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
            }`}
            onClick={(e) => handleHashNav(e, 'support')}
          >
            Support
          </a>
        </li>
      </ul>
    </nav>
  );
};

export default SidebarNav;
